/**
 * Finding the device a person actually chose, across reboots and re-plugs.
 *
 * `deviceId` alone is not enough to remember a microphone by. It is a hash the
 * browser salts per origin, and the salt is not forever: clearing site data,
 * revoking the permission, or a private window all produce a new id for the
 * same physical headset. Some platforms also hand out a fresh id when a USB
 * device is unplugged and plugged back in.
 *
 * So the choice is stored as id **and** label, and resolved in that order: the
 * id is exact when it still works, the label is what survives when it does not.
 */

export interface DevicePreference {
  deviceId?: string;
  label?: string;
}

export interface DeviceLike {
  deviceId: string;
  label: string;
}

/**
 * Find the preferred device among the ones currently attached.
 *
 * Returns undefined when the device is simply not here right now — unplugged
 * headphones, a webcam left at the office. ⚠️ That is *not* a reason to forget
 * the preference: it is exactly the case where forgetting is most annoying,
 * because the device usually comes back.
 */
export function resolvePreferredDevice<T extends DeviceLike>(
  devices: readonly T[],
  preference: DevicePreference | undefined,
): T | undefined {
  if (!preference?.deviceId && !preference?.label) return undefined;

  if (preference.deviceId) {
    const byId = devices.find((d) => d.deviceId === preference.deviceId);
    if (byId) return byId;
  }

  if (preference.label) {
    // Labels are not unique in theory ("USB Audio Device" twice); first match is
    // still a far better guess than falling back to the system default.
    const byLabel = devices.find(
      (d) => d.label && normalise(d.label) === normalise(preference.label!),
    );
    if (byLabel) return byLabel;
  }

  return undefined;
}

/**
 * Whether the preference needs rewriting after a resolve.
 *
 * The id changes under us (new salt, re-plug); when the label matched we adopt
 * the new id so the next lookup is exact again.
 */
export function shouldUpdatePreference(
  preference: DevicePreference | undefined,
  resolved: DeviceLike | undefined,
): boolean {
  if (!resolved) return false;
  return (
    preference?.deviceId !== resolved.deviceId ||
    (!!resolved.label && preference?.label !== resolved.label)
  );
}

/**
 * "default" is not a device, it is a pointer to whatever the OS picked. Storing
 * it as a preference would mean "follow the system", which is the one thing a
 * person who opened this menu has just decided against — but it still has to be
 * selectable, so it is kept as a preference with no label.
 */
export function isSystemDefault(deviceId: string | undefined): boolean {
  return deviceId === "default" || deviceId === "communications";
}

function normalise(label: string): string {
  // Windows decorates the same headset differently between enumerations
  // ("Headset (Jabra Evolve)" / "Headset Microphone (Jabra Evolve)"), and the
  // default entry is prefixed. Compare on the meat of the name.
  return label
    .toLowerCase()
    .replace(/^(default|communications)\s*-\s*/, "")
    .replace(/\s+/g, " ")
    .trim();
}
